import { supabase } from '@/lib/supabaseClient';
import type { GenerationStatus } from '@/types/history';
import type { PhotoshootDetail, PhotoshootShotEntry } from '@/types/photoshoot';

import { getSignedImageUrls } from './storage';

interface RawShoot {
  id: string;
  collection_id: string;
  selections: Record<string, string[]>;
  status: GenerationStatus;
  size: number;
}

interface RawShotRow {
  position: number;
  generations: {
    id: string;
    status: GenerationStatus;
    provider: string | null;
    generation_images: { storage_path: string; position: number }[];
  } | null;
}

/**
 * Результат фотосессии: заголовок серии и её кадры.
 *
 * Порядок задаёт photoshoot_generations.position, а не время создания —
 * так серия читается одинаково, даже если кадр пересоздавался. «Нет такой
 * серии» и «серия чужая» неотличимы намеренно: RLS отдаёт пустой результат
 * в обоих случаях, и разные сообщения выдали бы существование чужой записи.
 */
export async function fetchPhotoshootResult(
  photoshootId: string,
): Promise<PhotoshootDetail | null> {
  if (!supabase) {
    return null;
  }

  const { data: shootData, error: shootError } = await supabase
    .from('photoshoots')
    .select('id, collection_id, selections, status, size')
    .eq('id', photoshootId)
    .maybeSingle();

  if (shootError) {
    throw new Error('Не удалось загрузить фотосессию.');
  }

  if (!shootData) {
    return null;
  }

  const shoot = shootData as unknown as RawShoot;

  const { data: shotData, error: shotError } = await supabase
    .from('photoshoot_generations')
    .select('position, generations(id, status, provider, generation_images(storage_path, position))')
    .eq('photoshoot_id', photoshootId)
    .order('position', { ascending: true });

  if (shotError) {
    throw new Error('Не удалось загрузить фотосессию.');
  }

  const rows = ((shotData ?? []) as unknown as RawShotRow[])
    .filter((row): row is RawShotRow & { generations: NonNullable<RawShotRow['generations']> } =>
      Boolean(row.generations),
    )
    .sort((left, right) => left.position - right.position);

  const previewPaths = rows
    .map((row) => firstImagePath(row.generations.generation_images))
    .filter((path): path is string => Boolean(path));

  const signedUrls = await getSignedImageUrls(previewPaths);

  const shots: PhotoshootShotEntry[] = rows.map((row) => {
    const path = firstImagePath(row.generations.generation_images);

    return {
      position: row.position,
      generationId: row.generations.id,
      status: row.generations.status,
      thumbnailUrl: path ? (signedUrls.get(path) ?? null) : null,
    };
  });

  return {
    id: shoot.id,
    collectionId: shoot.collection_id,
    status: shoot.status,
    size: shoot.size,
    selections: shoot.selections,
    shots,
    provider: rows[0]?.generations.provider ?? null,
  };
}

function firstImagePath(images: { storage_path: string; position: number }[]): string | undefined {
  return [...images].sort((left, right) => left.position - right.position)[0]?.storage_path;
}
